import type { TypedMoney } from "@commercetools/platform-sdk";
import { cn } from "@babybjorn/ui";
import type { FC, HTMLAttributes, ReactNode } from "react";
import { moneyToNumber } from "../../../../utils/commercetools/money";
import AddToCartButton from "../../components/AddToCartButton";
import { BazaarWidget } from "../../components/BazaarVoiceWidget/BazaarVoiceWidget";
import Button from "../../components/general/Button";
import Payment from "../../components/general/Payment";
import { useHasBazaarVoice } from "../../hooks/use-bazaarvoice";
import type { buttonProps } from "../../templates/product-page/product-page.component";
import { formatMoney } from "../../utils/cart/currency";
import Icon from "../general/Icon";
import ScrollSpyItem from "../general/ScrollSpy/ScrollSpyItem";
import * as s from "./PriceModule.module.scss";

type PriceModuleProps = HTMLAttributes<HTMLElement> & {
	productName: string;
	productOptions: ReactNode;
	rawPrice: TypedMoney;
	inStock?: boolean;
	shippingInfo?: string;
	deliveryInfo?: string;
	giftwrapInfo?: string;
	addToCartLabel: string;
	inStockLabel?: string;
	outOfStockLabel?: string;
	waitListLabel?: string;
	waitListForm?: ReactNode;
	waitListToggleInput?: () => void;
	waitListShowInput?: boolean;
	waitListDisabled?: boolean;
	paymentMethods?: ReactNode[];
	addToCart: () => Promise<void>;
	shareClick?: () => void;
	sku: string;
	productBuyButtonId: string;
	buttons?: buttonProps;
	babyListButton?: ReactNode;
	myRegistryButton?: ReactNode;
	ariaLabel?: string;
	fewInStock?: boolean;
};

const PriceModule: FC<PriceModuleProps> = ({
	productName,
	productOptions,
	rawPrice,
	inStock,
	shippingInfo,
	deliveryInfo,
	giftwrapInfo,
	addToCartLabel,
	inStockLabel,
	outOfStockLabel,
	waitListLabel,
	waitListForm,
	waitListToggleInput,
	waitListShowInput,
	waitListDisabled,
	paymentMethods,
	addToCart,
	sku,
	productBuyButtonId,
	shareClick,
	buttons,
	babyListButton,
	myRegistryButton,
	ariaLabel,
	fewInStock,
	...rest
}) => {
	const numberOfButtons =
		(buttons ? buttons.length : 0) +
		(babyListButton ? 1 : 0) +
		(myRegistryButton ? 1 : 0);

	const hasCredits = shippingInfo || deliveryInfo || giftwrapInfo;
	const hasBazaarVoice = useHasBazaarVoice();

	return (
		<section className={s.wrapper} {...rest}>
			<div className={s.productContainer}>
				<header className={s.header}>
					{hasBazaarVoice && (
						<a href="#reviews" className={s.rating}>
							<BazaarWidget type="inline_rating" productSku={sku} />
						</a>
					)}
					<h1 className={s.title}>{productName}</h1>
				</header>
				{productOptions}
				<div
					itemScope
					itemProp="offers"
					itemType="https://schema.org/Offer"
					className={s.priceContainer}
				>
					<meta itemProp="priceCurrency" content={rawPrice.currencyCode} />
					<meta itemProp="price" content={moneyToNumber(rawPrice).toString()} />

					<p className={cn(s.price, { [s.outOfStockPrice]: !inStock })}>
						{formatMoney(rawPrice)}
					</p>
					<p
						className={cn(s.inStockText, {
							[s.outOfStockText]: !inStock,
							[s.fewInStockText]: fewInStock,
						})}
					>
						{inStock ? inStockLabel : outOfStockLabel}
					</p>
				</div>
				<ScrollSpyItem id={productBuyButtonId} style={{ width: "100%" }}>
					{inStock && (
						<AddToCartButton
							label={addToCartLabel}
							icon="cart"
							fullWidth
							onClick={addToCart}
							buttonType="large"
						/>
					)}
					{!inStock && !waitListDisabled && (
						<Button
							variant="outlined"
							label={waitListLabel}
							icon="edit"
							fullWidth
							buttonType="large"
							onClick={waitListToggleInput}
							className={cn({ [s.btnHide]: waitListShowInput })}
						/>
					)}
				</ScrollSpyItem>
				{waitListShowInput && waitListForm}
			</div>
			<div className={s.additionalsContainer}>
				<div className={s.additionalsFirstSection}>
					<div className={cn(s.infoContainer, { [s.alignRight]: !hasCredits })}>
						{hasCredits && (
							<div className={s.creditContainer}>
								{shippingInfo && (
									<span>
										<Icon icon="shipping" width={20} />
										<p className={s.text}>{shippingInfo}</p>
									</span>
								)}
								{deliveryInfo && (
									<span>
										<Icon icon="calendar" width={20} />
										<p className={s.text}>{deliveryInfo}</p>
									</span>
								)}
								{/* TODO: Remove this until we have a proper way to handle gift wrapping */}
								{/* only to be shown if gift wrapping is available */}
								{giftwrapInfo && (
									<span>
										<Icon icon="gift" width={20} />
										<p className={s.text}>{giftwrapInfo}</p>
									</span>
								)}
							</div>
						)}
						{shareClick && (
							<Button
								onClick={shareClick}
								icon="share-alt"
								buttonType="icon"
								variant="outlined"
								aria-label={ariaLabel}
							/>
						)}
					</div>
					<Payment priceModule className={s.payment}>
						{paymentMethods}
					</Payment>
				</div>
				{numberOfButtons > 0 && (
					<div
						className={cn(s.additionalButtons, {
							[s.oneButton]: numberOfButtons === 1,
							[s.oddButtonAmount]: numberOfButtons > 0 && numberOfButtons % 2,
						})}
					>
						{babyListButton}
						{myRegistryButton}
						{buttons?.map((button) => (
							<Button
								key={button.label}
								label={button.label}
								variant="outlined"
								icon={button.icon}
								href={button.href}
								target={button.target}
								alwaysSmall
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
};

export default PriceModule;
